import { chakra, Stack } from '@chakra-ui/react'
import type { ReactNode } from 'react'
import { LuChevronDown } from 'react-icons/lu'

const Details = chakra('details')
const Summary = chakra('summary')

/**
 * A folded part of a page (`<details>`), drawn as a disclosure box (mobile review): a bordered row with the title and a
 * chevron at its end that turns when it is open, so it reads as "more here" and not as a link to another page. The
 * whole 48 px row is the target. The text stays in the page.
 */
export function Fold({ title, children }: { title: ReactNode; children: ReactNode }) {
  return (
    // A content surface with the control outline, so the closed row reads as something to open, on the canvas and in a
    // card alike (R113).
    <Details
      borderWidth='1px'
      borderColor='border.input'
      borderRadius='md'
      bg='bg'
      css={{ '&[open]': { paddingBottom: '4' } }}
    >
      <Summary
        minH='12'
        display='flex'
        alignItems='center'
        gap={3}
        px={4}
        py={2}
        cursor='pointer'
        fontWeight='bold'
        borderRadius='md'
        listStyleType='none'
        _hover={{ bg: 'bg.muted' }}
        css={{
          '&::-webkit-details-marker': { display: 'none' },
          '&::marker': { content: '""' },
          'details[open] > & > span:last-child': { transform: 'rotate(180deg)' },
        }}
      >
        {title}
        <chakra.span
          display='inline-flex'
          marginInlineStart='auto'
          flexShrink={0}
          css={{
            transition: 'transform 150ms ease',
            '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
          }}
        >
          <LuChevronDown aria-hidden />
        </chakra.span>
      </Summary>
      <Stack gap={4} pt={2} px={4}>
        {children}
      </Stack>
    </Details>
  )
}
